import { memo, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  CalendarDays,
  Loader2,
  MousePointerClick,
  Plus,
} from 'lucide-react';
import { ApiError } from '../../lib/api';
import {
  composicionApi,
  composicionKeys,
  type EdicionSesion,
  type HerramientaCatalogo,
  type MoverActividad,
  type SesionArbol,
} from '../../lib/composicion';
import { useArrastreCompositor } from '../../hooks/useArrastre';
import { ATRIBUTO_DESTINO } from '../../hooks/useArrastrePuntero';
import { FichaActividad, type OpcionSesion } from './FichaActividad';
import { useResaltado } from './ValidadorPanel';

const claseCampo =
  'rounded-md border border-border-strong bg-surface-sunken px-2 py-1 font-sans ' +
  'text-sm tabular-nums text-content transition-colors duration-fast ' +
  'focus-visible:outline-none focus-visible:shadow-focus';

/** "HH:MM[:SS]" → "HH:MM" para los inputs time. */
function horaCorta(hora: string | null): string {
  return hora ? hora.slice(0, 5) : '';
}

/** Minutos entre hora_ini y hora_fin, o null si la ventana no está completa. */
function minutosVentana(ini: string | null, fin: string | null): number | null {
  if (!ini || !fin) return null;
  const [h1, m1] = horaCorta(ini).split(':').map(Number);
  const [h2, m2] = horaCorta(fin).split(':').map(Number);
  if ([h1, m1, h2, m2].some((n) => !Number.isFinite(n))) return null;
  const delta = h2 * 60 + m2 - (h1 * 60 + m1);
  return delta > 0 ? delta : null;
}

/**
 * Tarjeta de una sesión dentro de su columna de fase: agenda (fecha + ventana
 * horaria, PATCH al blur), suma de minutos contra la ventana (solo el dato,
 * en text-warning si excede — el validador llega en el Lote 3) y la agenda
 * vertical de FichaActividad. Con herramienta seleccionada muestra el punto
 * de inserción «Agregar aquí». Además es TARGET del drag nativo (Lote 2b,
 * aditivo al clic): soltar una herramienta agrega al final; soltar una
 * ficha 'preparada' la mueve al final de esta sesión.
 */
export const TarjetaSesion = memo(function TarjetaSesion({
  sesion,
  indice,
  procesoId,
  herramientaSeleccionada,
  opcionesSesion,
}: {
  sesion: SesionArbol;
  /** Número de la sesión dentro de su fase (1..n). */
  indice: number;
  procesoId: string;
  herramientaSeleccionada: HerramientaCatalogo | null;
  opcionesSesion: OpcionSesion[];
}) {
  const qc = useQueryClient();
  const invalidar = () =>
    void qc.invalidateQueries({ queryKey: composicionKeys.arbol(procesoId) });

  // Agenda editable: estado local resincronizado con el árbol del servidor.
  const [fecha, setFecha] = useState(sesion.fecha ?? '');
  useEffect(() => setFecha(sesion.fecha ?? ''), [sesion.fecha]);
  const [horaIni, setHoraIni] = useState(horaCorta(sesion.hora_ini));
  useEffect(() => setHoraIni(horaCorta(sesion.hora_ini)), [sesion.hora_ini]);
  const [horaFin, setHoraFin] = useState(horaCorta(sesion.hora_fin));
  useEffect(() => setHoraFin(horaCorta(sesion.hora_fin)), [sesion.hora_fin]);

  const editarMut = useMutation({
    mutationFn: (cuerpo: EdicionSesion) => composicionApi.editarSesion(sesion.id, cuerpo),
    onSuccess: invalidar,
  });
  const agregarMut = useMutation({
    mutationFn: (herramienta_codigo: string) =>
      composicionApi.agregarActividad(sesion.id, { herramienta_codigo }),
    onSuccess: invalidar,
  });
  // Mover por DROP (la ficha arrastrada puede venir de OTRA sesión, por eso
  // el id viaja como argumento; el clic ↑/↓ de la ficha sigue usando el suyo).
  const moverMut = useMutation({
    mutationFn: (args: { id: string; cuerpo: MoverActividad }) =>
      composicionApi.moverActividad(args.id, args.cuerpo),
    onSuccess: invalidar,
  });

  const accionError =
    (editarMut.error as ApiError | null)?.detail ??
    (agregarMut.error as ApiError | null)?.detail ??
    (moverMut.error as ApiError | null)?.detail ??
    null;

  /** PATCH al blur solo si el valor cambió y no quedó vacío. */
  function guardar(campo: keyof EdicionSesion, valor: string, previo: string) {
    if (!valor || valor === previo) return;
    editarMut.mutate({ [campo]: valor });
  }

  const ventana = minutosVentana(sesion.hora_ini, sesion.hora_fin);
  const excede = ventana != null && sesion.suma_min > ventana;
  const actividades = [...sesion.actividades].sort((a, b) => a.orden - b.orden);

  // ---- Resaltado desde el ValidadorPanel (Lote 3) ----
  // La tarjeta se ilumina solo si la advertencia apunta a la SESIÓN (si trae
  // actividad_id, el ring lo pone la FichaActividad). Se apaga sola: la página
  // limpia el contexto a los 3 s.
  const resaltado = useResaltado();
  const resaltada = resaltado?.sesionId === sesion.id && resaltado?.actividadId == null;
  const refSeccion = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (resaltada) {
      refSeccion.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [resaltada, resaltado]);

  // ---- Drag nativo (Lote 2b): esta tarjeta como target de drop ----
  const arrastre = useArrastreCompositor();
  const itemEnVuelo = arrastre?.item ?? null;
  // Contador enter/leave: el dragleave dispara al pasar sobre hijos, el par
  // enter(hijo)/leave(padre) se cancela y la iluminación no parpadea.
  const [dentroDrag, setDentroDrag] = useState(0);
  const iluminada = itemEnVuelo != null && dentroDrag > 0;

  /** Un drop = UNA mutación: agregar (herramienta) o mover al final (actividad). */
  function soltarSobreSesion(item: NonNullable<typeof itemEnVuelo>) {
    if (item.tipo === 'herramienta') {
      agregarMut.mutate(item.codigo);
      return;
    }
    const yaAqui = item.sesionId === sesion.id;
    const ultima = actividades[actividades.length - 1];
    if (yaAqui && ultima?.id === item.id) return; // ya está al final: no-op
    moverMut.mutate({
      id: item.id,
      cuerpo: {
        sesion_id: sesion.id,
        orden: yaAqui ? actividades.length : actividades.length + 1,
      },
    });
  }

  // El destino se registra para la vía del dedo: el hook busca la sesión bajo
  // el punto donde se suelta y llama exactamente esta acción. La misma que usa
  // el arrastre nativo, sin lógica duplicada.
  useEffect(() => {
    if (!arrastre) return;
    return arrastre.puntero.registrarDestino(sesion.id, soltarSobreSesion);
  });

  const sobreEsta = arrastre?.puntero.sobre === sesion.id;

  return (
    <section
      ref={refSeccion}
      {...{ [ATRIBUTO_DESTINO]: sesion.id }}
      onDragEnter={(e) => {
        if (!itemEnVuelo) return;
        e.preventDefault();
        setDentroDrag((n) => n + 1);
      }}
      onDragOver={(e) => {
        if (!itemEnVuelo) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDragLeave={() => {
        if (!itemEnVuelo) return;
        setDentroDrag((n) => Math.max(0, n - 1));
      }}
      onDrop={(e) => {
        setDentroDrag(0);
        if (!itemEnVuelo) return;
        e.preventDefault();
        soltarSobreSesion(itemEnVuelo);
        arrastre?.terminarArrastre();
      }}
      className={`rounded-lg border bg-surface-raised transition-colors duration-fast ${
        iluminada || sobreEsta ? 'border-primary' : 'border-border'
      } ${sobreEsta ? 'ring-2 ring-primary' : ''} ${resaltada ? 'ring-2 ring-warning' : ''}`}
    >
      <header className="border-b border-border px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <h4 className="flex items-center gap-1.5 font-display text-sm font-semibold text-content">
            <CalendarDays className="h-4 w-4 text-cyan" aria-hidden="true" />
            Sesión {indice}
          </h4>
          <span
            className={`flex items-center gap-1 font-sans text-xs tabular-nums ${
              excede ? 'text-warning' : 'text-content-muted'
            }`}
            title={
              excede
                ? 'Las actividades suman más minutos que la ventana horaria de la sesión.'
                : 'Minutos que suman las actividades frente a la ventana horaria.'
            }
          >
            {excede && <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            Suma {sesion.suma_min} min
            {ventana != null && ` · ventana ${ventana} min`}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            onBlur={() => guardar('fecha', fecha, sesion.fecha ?? '')}
            aria-label={`Fecha de la sesión ${indice}`}
            className={claseCampo}
          />
          <span className="flex items-center gap-1">
            <input
              type="time"
              value={horaIni}
              onChange={(e) => setHoraIni(e.target.value)}
              onBlur={() => guardar('hora_ini', horaIni, horaCorta(sesion.hora_ini))}
              aria-label={`Hora de inicio de la sesión ${indice}`}
              className={claseCampo}
            />
            <span className="font-sans text-xs text-content-muted">a</span>
            <input
              type="time"
              value={horaFin}
              onChange={(e) => setHoraFin(e.target.value)}
              onBlur={() => guardar('hora_fin', horaFin, horaCorta(sesion.hora_fin))}
              aria-label={`Hora de fin de la sesión ${indice}`}
              className={claseCampo}
            />
          </span>
          {editarMut.isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-content-muted" aria-hidden="true" />
          )}
        </div>
      </header>

      <div className="flex flex-col gap-2 px-3 py-2.5">
        {actividades.length === 0 && !herramientaSeleccionada && (
          <p className="flex items-start gap-1.5 rounded-md border border-dashed border-border px-3 py-2.5 font-sans text-xs text-content-muted">
            <MousePointerClick className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Elegí una herramienta del catálogo y agregala aquí.
          </p>
        )}

        {actividades.map((a, i) => (
          <FichaActividad
            key={a.id}
            actividad={a}
            sesionId={sesion.id}
            procesoId={procesoId}
            esPrimera={i === 0}
            esUltima={i === actividades.length - 1}
            opcionesSesion={opcionesSesion}
            herramientaSeleccionada={herramientaSeleccionada}
            alSoltarActividad={(idArrastrada, orden) =>
              moverMut.mutate({
                id: idArrastrada,
                cuerpo: { sesion_id: sesion.id, orden },
              })
            }
          />
        ))}

        {herramientaSeleccionada && (
          <button
            type="button"
            onClick={() => agregarMut.mutate(herramientaSeleccionada.codigo)}
            disabled={agregarMut.isPending}
            className="flex min-h-touch w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-primary px-3 font-sans text-sm font-semibold text-primary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus disabled:pointer-events-none disabled:opacity-50"
          >
            {agregarMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Plus className="h-4 w-4" aria-hidden="true" />
            )}
            Agregar aquí · {herramientaSeleccionada.nombre}
          </button>
        )}

        {accionError && (
          <p className="flex items-start gap-1.5 font-sans text-xs text-error">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {accionError}
          </p>
        )}
      </div>
    </section>
  );
});
